import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { IdLabel } from '../../components/atoms.js';
import { TestCasePicker, type TestCasePickerCandidate } from '../../components/test-case-picker.js';
import { ClipTooltip } from '../../components/tooltip.js';
import { catalogApi } from '../../lib/catalog-api.js';
import { defectsApi } from '../../lib/defects-api.js';
import { pickLang } from '../../lib/pick-lang.js';

// #105: „blockiert außerdem" — n:m-Liste zusätzlicher Testfälle NEBEN dem primären
// defect.test_case_id. Fehler beim Verknüpfen (already_linked/is_primary_case, beides 409) laufen
// bewusst OHNE `meta.errorToast: false` über den globalen Mutation-Toast (query-client.ts) — anders
// als bei der Transition-Mutation (#87) gibt es hier keinen eigenen Dialog, der den Fehler schon
// sichtbar machen würde, und beide Codes tragen inzwischen einen benannten Text (lib/errors.ts).
//
// Review-Fund (Important 2, Task 5): der Picker war hier zunächst als Fast-Kopie von
// AddMemberPicker (catalog/e2e-tab.tsx) eigenständig nachgebaut. Beide Aufrufer nutzen jetzt
// stattdessen `TestCasePicker` (components/test-case-picker.tsx) — dieselbe Combobox-/
// Filterlogik, nur mit eigenen Beschriftungen (#82-Muster einmal, nicht zweimal).
export function AffectedSection({
  projectId,
  defectId,
  primaryTestCaseId,
}: {
  projectId: string;
  defectId: string;
  primaryTestCaseId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const queryClient = useQueryClient();

  const { data: affected } = useQuery({
    queryKey: ['defect-affected', projectId, defectId],
    queryFn: () => defectsApi.affected(projectId, defectId),
    enabled: Boolean(projectId && defectId),
  });
  // Gleicher Query-Key wie im Anlege-Dialog (defect-create-dialog.tsx) — dieselbe Liste, meist
  // schon im Cache.
  const { data: testCases } = useQuery({
    queryKey: ['catalog-testcases', projectId],
    queryFn: () => catalogApi.listTestCases(projectId),
    enabled: Boolean(projectId),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['defect-affected', projectId, defectId] });

  const add = useMutation({
    mutationFn: (testCaseId: string) => defectsApi.addAffected(projectId, defectId, testCaseId),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (testCaseId: string) => defectsApi.removeAffected(projectId, defectId, testCaseId),
    onSuccess: invalidate,
  });

  const rows = affected ?? [];
  const linkedIds = new Set(rows.map((a) => a.test_case_id));
  // Der primäre Testfall und bereits verknüpfte Testfälle fallen aus der Kandidatenliste heraus —
  // beide würden serverseitig ohnehin mit 409 abgelehnt (is_primary_case/already_linked). Das
  // verhindert den Fehlerfall im Normalbetrieb, ohne ihn unsichtbar zu machen: eine Race (zwei
  // Nutzer gleichzeitig) trifft weiterhin den benannten Fehlertext statt eines stillen 500.
  const candidates: TestCasePickerCandidate[] = (testCases ?? [])
    .filter((tc) => tc.test_case_id !== primaryTestCaseId && !linkedIds.has(tc.test_case_id))
    .map((tc) => ({
      test_case_id: tc.test_case_id,
      human_id: tc.human_id,
      title_de: tc.title_de,
      title_en: tc.title_en,
    }));

  return (
    <div className="space-y-2 rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <h3 className="text-sm font-semibold text-text-primary">{t('defects.affected.title')}</h3>
      {rows.length === 0 ? (
        <div className="text-sm text-text-muted">{t('defects.affected.empty')}</div>
      ) : (
        <ul className="space-y-1">
          {rows.map((a) => (
            <li key={a.test_case_id} className="flex items-center gap-2 text-sm">
              <IdLabel id={a.human_id ?? '—'} />
              {/* #95: Titel steht hier neben der Entfernen-Schaltflaeche, nicht IN ihr — kein
                  fokussierbares Kind zum Umschliessen. tabIndex macht den Titel selbst zum
                  Fokus-Ziel fuer die ClipTooltip-Blase. */}
              <ClipTooltip
                text={pickLang(lang, a.title_de, a.title_en) || '—'}
                className="min-w-0 flex-1"
              >
                {/* biome-ignore lint/a11y/noNoninteractiveTabindex: reiner Lesetext ohne
                    Klickziel — der Tab-Stop existiert nur fuer die ClipTooltip-Blase (#95). */}
                <span data-clip tabIndex={0} className="block truncate text-text-body">
                  {pickLang(lang, a.title_de, a.title_en) || '—'}
                </span>
              </ClipTooltip>
              <button
                type="button"
                onClick={() => remove.mutate(a.test_case_id)}
                disabled={remove.isPending}
                aria-label={t('defects.affected.remove')}
                title={t('defects.affected.remove')}
                className="rounded-btn px-1 text-text-muted hover:text-status-fail"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      <TestCasePicker
        label={t('defects.affected.searchLabel')}
        placeholder={t('defects.affected.searchPlaceholder')}
        addLabel={t('defects.affected.add')}
        pending={add.isPending}
        candidates={candidates}
        onAdd={(testCaseId) => add.mutate(testCaseId)}
      />
    </div>
  );
}
